import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { DataManager } from '../../core/data-manager';
import { formatImporto, formatTime, optValue, optPayload, oraInMinuti, refPayload, umanizza } from '../../lib/format';
import { Field, BoolPill, ConfirmModal, FormModal, messaggioErrore } from './common';
import Select from '../../components/Select';

const VUOTA = { nome: '', tipo: 'slot', durata_ore: 4, prezzo: '0', ordine: 0, attiva: 1 };

const TIPI = [
    { value: 'slot', label: 'Fascia oraria' },
    { value: 'giornata', label: 'Giornata intera' }
];

/**
 * Ore della giornata intera: tutta la finestra di apertura della sala. Al
 * BookingEngine non servono - la giornata la misura dagli orari della sala -
 * ma si salvano lo stesso, perche' la colonna e' obbligatoria e in elenco
 * dicono quanto dura.
 */
function oreApertura(sala) {
    const minuti = oraInMinuti(sala.orario_chiusura) - oraInMinuti(sala.orario_apertura);
    return Math.floor(minuti / 60);
}

/**
 * Tariffe di una sala. Chi prenota sceglie quante ore (o la giornata
 * intera) e il prezzo viene dalla durata:
 *  - una «fascia oraria» vale fino a `durata_ore` ore comprese; tra piu'
 *    fasce vale la piu' corta che copre le ore chieste;
 *  - la «giornata intera» vale per la giornata e per ogni durata oltre
 *    l'ultima fascia. Senza, oltre l'ultima fascia non si prenota.
 * Es. «Fino a 4 ore» a 50 euro e «Giornata intera» a 100.
 * Senza almeno una tariffa attiva la sala non e' prenotabile.
 *
 * Vive dentro la pagina della sala; si crea e si modifica in un modale,
 * come gli appartamenti nella palazzina.
 */
export default function TariffeSala({ sala, tariffe, onChanged }) {
    const [form, setForm] = useState(null);
    const [inModifica, setInModifica] = useState(null);
    const [saving, setSaving] = useState(false);
    const [daEliminare, setDaEliminare] = useState(null);
    const [working, setWorking] = useState(false);

    const ordinate = useMemo(
        () => [...tariffe].sort((a, b) => (a.ordine || 0) - (b.ordine || 0)),
        [tariffe]
    );

    function aggiorna(campo, valore) {
        setForm(prev => ({ ...prev, [campo]: valore }));
    }

    /**
     * Cambiando tipo cambia chi decide la durata: «giornata» la deriva dagli
     * orari della sala, «slot» torna a quella scelta a mano (tenuta da parte
     * in `durata_slot`, che non finisce nel payload).
     */
    function cambiaTipo(valore) {
        setForm(prev => valore === 'giornata'
            ? { ...prev, tipo: valore, durata_slot: prev.durata_ore, durata_ore: oreApertura(sala) }
            : { ...prev, tipo: valore, durata_ore: prev.durata_slot ?? VUOTA.durata_ore });
    }

    function apriNuova() {
        setInModifica(null);
        setForm({ ...VUOTA, ordine: ordinate.length });
    }

    function apriModifica(t) {
        const tipo = optValue(t.tipo) || 'slot';
        setInModifica(t);
        setForm({
            nome: t.nome || '',
            tipo,
            // una giornata si riallinea agli orari correnti della sala
            durata_ore: tipo === 'giornata' ? oreApertura(sala) : t.durata_ore,
            durata_slot: t.durata_ore,
            prezzo: String(t.prezzo),
            ordine: t.ordine || 0,
            attiva: parseInt(t.attiva, 10) === 1 ? 1 : 0
        });
    }

    async function salva(e) {
        e.preventDefault();
        e.stopPropagation();
        if (!form.nome.trim()) {
            toast.error('Il nome della tariffa è obbligatorio.', { theme: 'colored' });
            return;
        }
        const durata = form.tipo === 'giornata' ? oreApertura(sala) : parseInt(form.durata_ore, 10);
        if (!durata || durata < 1) {
            toast.error(
                form.tipo === 'giornata'
                    ? 'Gli orari di apertura della sala non coprono neppure un\'ora.'
                    : "La durata deve essere di almeno un'ora.",
                { theme: 'colored' }
            );
            return;
        }

        const payload = {
            idSala: refPayload(sala.id),
            nome: form.nome.trim(),
            tipo: optPayload(form.tipo),
            durata_ore: durata,
            prezzo: parseFloat(String(form.prezzo).replace(',', '.')) || 0,
            ordine: parseInt(form.ordine, 10) || 0,
            attiva: parseInt(form.attiva, 10)
        };

        setSaving(true);
        try {
            const res = inModifica
                ? await DataManager.updateItem('tariffa', payload, inModifica.id)
                : await DataManager.insertItem('tariffa', payload);
            if (res && res.success === 1) {
                toast.success(inModifica ? 'Tariffa aggiornata.' : 'Tariffa creata.', { theme: 'colored' });
                setForm(null);
                setInModifica(null);
                onChanged();
            }
            else {
                toast.error(messaggioErrore(res, 'Salvataggio non riuscito.'), { theme: 'colored' });
            }
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setSaving(false);
        }
    }

    async function elimina() {
        setWorking(true);
        try {
            const res = await DataManager.deleteItem('tariffa', daEliminare.id);
            if (res && res.success === 1) {
                toast.success('Tariffa eliminata.', { theme: 'colored' });
                setDaEliminare(null);
                onChanged();
            }
            else {
                toast.error(messaggioErrore(res), { theme: 'colored' });
            }
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setWorking(false);
        }
    }

    return (
        <div className="lv-subsection">
            <div className="lv-subsection-head">
                <h3 className="lv-subsection-title">Tariffe</h3>
                <button type="button" className="lv-btn lv-btn-ghost lv-btn-sm" onClick={apriNuova}>
                    <FontAwesomeIcon icon={['fas', 'plus']} className="me-2" />
                    Aggiungi tariffa
                </button>
            </div>

            {ordinate.length === 0 ? (
                <div className="lv-note">
                    Nessuna tariffa definita: senza tariffe attive la sala non è prenotabile.
                </div>
            ) : (
                <div className="lv-table-wrap">
                    <table className="lv-table">
                        <thead>
                            <tr>
                                <th>Tariffa</th>
                                <th>Tipo</th>
                                <th>Vale per</th>
                                <th>Prezzo</th>
                                <th>Stato</th>
                                <th>Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ordinate.map(t => (
                                <tr key={t.id}>
                                    <td data-label="Tariffa">{t.nome}</td>
                                    <td data-label="Tipo">{umanizza(optValue(t.tipo))}</td>
                                    <td data-label="Vale per">
                                        {optValue(t.tipo) === 'giornata'
                                            ? 'Giornata intera e oltre le fasce'
                                            : `Fino a ${t.durata_ore} h`}
                                    </td>
                                    <td data-label="Prezzo">{formatImporto(t.prezzo)}</td>
                                    <td data-label="Stato">
                                        <BoolPill value={t.attiva} labelOn="Attiva" labelOff="Non attiva" />
                                    </td>
                                    <td data-label="Azioni">
                                        <div className="lv-row-actions">
                                            <button
                                                type="button"
                                                className="lv-btn lv-btn-ghost lv-btn-sm"
                                                onClick={() => apriModifica(t)}
                                            >
                                                Modifica
                                            </button>
                                            <button
                                                type="button"
                                                className="lv-btn lv-btn-ghost lv-btn-sm lv-btn-danger-ghost"
                                                onClick={() => setDaEliminare(t)}
                                            >
                                                Elimina
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <FormModal
                show={Boolean(form)}
                title={inModifica ? `Tariffa «${inModifica.nome}»` : 'Nuova tariffa'}
                onClose={() => setForm(null)}
                onSubmit={salva}
                saving={saving}
                submitLabel={inModifica ? 'Salva tariffa' : 'Crea tariffa'}
            >
                {form && (
                    <>
                        <Field label="Nome" required hint="Es. «Fino a 4 ore», «Giornata intera».">
                            <input className="lv-input" value={form.nome} onChange={e => aggiorna('nome', e.target.value)} />
                        </Field>
                        <Field label="Tipo">
                            <Select value={form.tipo} onChange={cambiaTipo} options={TIPI} />
                        </Field>
                        {form.tipo === 'giornata' ? (
                            <Field
                                label="Durata (ore)"
                                hint={`Vale per la giornata intera (${formatTime(sala.orario_apertura)}–${formatTime(sala.orario_chiusura)}) e per ogni durata oltre le fasce orarie.`}
                            >
                                <input className="lv-input" value={`${oreApertura(sala)} h`} readOnly disabled />
                            </Field>
                        ) : (
                            <Field
                                label="Fino a (ore)"
                                required
                                hint={`Vale per le prenotazioni fino a ${parseInt(form.durata_ore, 10) || 'N'} ore comprese.`}
                            >
                                <input
                                    type="number"
                                    min="1"
                                    className="lv-input"
                                    value={form.durata_ore}
                                    onChange={e => aggiorna('durata_ore', e.target.value)}
                                />
                            </Field>
                        )}
                        <Field label="Prezzo">
                            <input
                                className="lv-input"
                                value={form.prezzo}
                                onChange={e => aggiorna('prezzo', e.target.value)}
                            />
                        </Field>
                        <Field label="Ordine" hint="Posizione nell'elenco. Con più giornate intere attive vale la prima.">
                            <input
                                type="number"
                                min="0"
                                className="lv-input"
                                value={form.ordine}
                                onChange={e => aggiorna('ordine', e.target.value)}
                            />
                        </Field>
                        <Field label="Stato">
                            <Select
                                value={form.attiva}
                                onChange={v => aggiorna('attiva', v)}
                                options={[
                                    { value: '1', label: 'Attiva' },
                                    { value: '0', label: 'Non attiva' }
                                ]}
                            />
                        </Field>
                    </>
                )}
            </FormModal>

            <ConfirmModal
                show={Boolean(daEliminare)}
                title="Eliminare la tariffa?"
                message={daEliminare
                    ? `«${daEliminare.nome}» non sarà più proponibile. Le prenotazioni già fatte restano.`
                    : ''}
                confirmLabel="Elimina"
                danger
                working={working}
                onConfirm={elimina}
                onClose={() => setDaEliminare(null)}
            />
        </div>
    );
}
